<template lang="pug">
  button.button.wt-button(v-if="!isRouterLink" :class="classArray()" :type="type" :disabled="disabled")
    <!-- @slot Use this slot for button content -->
    slot(name='icon')
    slot
  RouterLink.button.wt-button.wt-button-router-link(
    v-else
    :class="classArray()"
    :to="disabled ? '' : to"
    :disabled="disabled"
  )
    slot
</template>

<script>
import { boldMixin, semiBoldMixin } from "@/mixins/textWeightMixin";

const OUTLINE_CLASS = "is-outlined";
const SELECTED_CLASS = "is-selected";

const COLORS = {
  primary: {
    value: "primary",
    buttonClass: "is-primary",
    fontColorClass: "has-text-primary"
  },
  green: {
    value: "green",
    buttonClass: "is-success",
    fontColorClass: "has-text-green"
  },
  red: {
    value: "red",
    buttonClass: "is-danger",
    fontColorClass: "has-text-danger"
  },
  lightgrey: {
    value: "lightGrey",
    buttonClass: "is-light-grey",
    fontColorClass: "has-text-grey-light"
  },
  grey: {
    value: "grey",
    buttonClass: "is-grey",
    fontColorClass: "has-text-grey"
  },
  darkgrey: {
    value: "darkgrey",
    buttonClass: "is-grey",
    fontColorClass: "has-text-grey-dark"
  },

  white: {
    value: "white",
    buttonClass: "is-white"
  },
  darkpurple: {
    value: "darkpurple",
    buttonClass: "is-darkpurple"
  },
  darkerpurple: {
    value: "darkerpurple",
    buttonClass: "is-darkerpurple"
  },
  black: {
    value: "black",
    buttonClass: "is-black",
    fontColorClass: "has-text-white"
  }
};

const SIZES = {
  small: "is-small",
  normal: "",
  medium: "is-medium",
  large: "is-large"
};

export default {
  name: "WtButton",

  mixins: [boldMixin, semiBoldMixin],

  props: {
    /**
     * `primary, green, red, grey, lightgrey, white`
     */
    color: {
      type: String,
      default: "primary",
      validator(color) {
        return Object.keys(COLORS).indexOf(color) !== -1;
      }
    },

    type: {
      type: String,
      default: "button"
    },

    /**
     * `normal, large, small`
     *  defaults to large when isToggle is true
     */
    size: {
      type: String,
      default: "normal"
    },

    /**
     * Adds `is-selected` modifier
     */
    selected: {
      type: Boolean,
      default: false
    },

    /**
     * Part of toggle group
     */
    isToggle: {
      type: Boolean,
      default: false
    },

    /**
     * adds `is-outlined` modifier
     */
    outlined: {
      type: Boolean,
      default: false
    },

    /**
     * will use the background color as text color
     */
    isText: {
      type: Boolean,
      default: false
    },

    /**
     * remove underline styling from isText
     */
    isTextUnderlined: {
      type: Boolean,
      default: true
    },

    isRounded: {
      type: Boolean,
      default: false
    },

    /**
     * will use the vue-router `RouterLink` component
     */
    isRouterLink: {
      type: Boolean,
      default: false
    },

    loading: {
      type: Boolean,
      default: false
    },

    /**
     * only works when `isRouterLink` is set to true
     * location to pass to RouterLink via it's `to` prop
     * https://router.vuejs.org/api/#router-link
     */
    to: {
      type: [String, Object],
      default: ""
    },

    fullwidth: {
      type: Boolean,
      default: false
    },

    disabled: {
      type: Boolean,
      default: false
    }
  },

  methods: {
    classArray() {
      return [
        this.sizeClass(),
        this.backgroundColorClass(),
        this.outlineClass(),
        this.selectedClass(),
        this.textClass(),
        this.roundedClass(),
        this.textColorClass(),
        this.boldClass(),
        this.semiboldClass(),
        this.loadingClass(),
        this.fullwidthClass()
      ];
    },

    sizeClass() {
      return SIZES[this.size];
    },

    fullwidthClass() {
      return this.fullwidth ? "is-fullwidth" : "";
    },

    backgroundColorClass() {
      let classStr = COLORS[this.color].buttonClass;

      if (this.isDeactivatedToggle() || this.isText) {
        classStr = "";
      }

      if (this.isText) {
        classStr = COLORS.white.buttonClass;
      }

      return classStr;
    },

    outlineClass() {
      let outlineClassStr = "";

      if (this.isDeactivatedToggle() || this.outlined) {
        outlineClassStr = OUTLINE_CLASS;
      }

      return outlineClassStr;
    },

    loadingClass() {
      return this.loading ? "is-loading" : "";
    },

    selectedClass() {
      return this.selected ? SELECTED_CLASS : "";
    },

    textClass() {
      let textClass = this.isText ? "is-text" : "";
      textClass += this.isTextUnderlined ? "" : " has-text-not-underlined";
      return textClass;
    },

    roundedClass() {
      return this.isRounded ? "is-rounded" : "";
    },

    textColorClass() {
      if (this.isText) {
        if (this.disabled) {
          return COLORS.lightgrey.fontColorClass;
        } else {
          return COLORS[this.color].fontColorClass;
        }
      }
      return "";
    },

    isDeactivatedToggle() {
      return this.isToggle && !this.selected;
    }
  }
};
</script>

<style lang="sass" scoped>
.wt-button
  &.is-light-grey
    background-color: $grey-lighter
    border-color: $grey-lighter
    color: $white
  &.is-grey:hover
    background-color: $grey-lighter
  &.has-text-not-underlined
    text-decoration: none !important
</style>
